import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Button, Intent } from '@blueprintjs/core';
import * as DataList from 'components/DataList';
import Toast from 'helpers/toaster';

export const LINE_ITEM_TYPES = [
  { value: 'percentage_of_material', label: 'Percentage of materials', unit: '%' },
  { value: 'percentage_of_labour', label: 'Percentage of labor', unit: '%' },
  { value: 'percentage_of_materials_labour', label: 'Percentage of materials and labor', unit: '%' },
  { value: 'dollar_value', label: 'Dollar amount', unit: '$' },
];

const FIELDS = ['description', 'line_item_type', 'value'];

const draftOf = (lineItem) => FIELDS.reduce((draft, key) => ({
  ...draft,
  [key]: lineItem[key] === null || lineItem[key] === undefined ? '' : String(lineItem[key]),
}), {});

// One default line item as an editable row (components/Preference/DefaultLineItems.js).
// Edits a copy; Save sends it, the trash can asks the list to confirm a delete.
@inject('preferenceStore') @observer
class DefaultItem extends Component {
  @observable draft = draftOf(this.props.line_item);

  componentDidUpdate(prevProps) {
    // Saved (the store returns fresh line items): start from what was saved.
    if (prevProps.line_item !== this.props.line_item) {
      this.draft = draftOf(this.props.line_item);
    }
  }

  get changed() {
    const saved = draftOf(this.props.line_item);
    return FIELDS.some((key) => saved[key] !== this.draft[key]);
  }

  set(key, value) {
    this.draft = { ...this.draft, [key]: value };
  }

  save = () => {
    const { line_item: lineItem, preferenceStore } = this.props;
    preferenceStore.updateDefaultLineItem({ ...lineItem, ...this.draft })
      .then(() => {
        Toast.show({ message: 'Line item saved.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const reason = error && error.response && error.response.data && error.response.data.errors;
        Toast.show({ message: reason || "Couldn't save the line item. Try again.", intent: Intent.DANGER });
      });
  };

  render() {
    const { line_item: lineItem, onDelete } = this.props;
    const type = LINE_ITEM_TYPES.find((option) => option.value === this.draft.line_item_type);
    const name = this.draft.description || 'line item';
    const id = `line-item-${lineItem.id}`;
    return (
      <DataList.Row>
        <DataList.Cell area="description">
          <input
            id={`${id}-description`}
            type="text"
            className="bp3-input bp3-fill"
            aria-label="Description"
            placeholder="e.g. Shop supplies"
            value={this.draft.description}
            onChange={(event) => this.set('description', event.target.value)}
          />
        </DataList.Cell>
        <DataList.Cell area="type">
          <div className="bp3-html-select bp3-fill">
            <select
              aria-label="Type"
              value={this.draft.line_item_type}
              onChange={(event) => this.set('line_item_type', event.target.value)}
            >
              <option value="" disabled>Choose a type</option>
              {LINE_ITEM_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        </DataList.Cell>
        <DataList.Cell area="value">
          <div className="dli-value">
            {type && type.unit === '$' && <span className="dli-value__unit" aria-hidden="true">$</span>}
            <input
              type="number"
              inputMode="decimal"
              className="bp3-input bp3-fill"
              aria-label="Value"
              placeholder="0"
              value={this.draft.value}
              onChange={(event) => this.set('value', event.target.value)}
            />
            {type && type.unit === '%' && <span className="dli-value__unit" aria-hidden="true">%</span>}
          </div>
        </DataList.Cell>
        <DataList.Cell area="actions">
          <div className="dli-actions">
            <Button
              className="sp-button"
              small={true}
              text="Save"
              disabled={!this.changed}
              onClick={this.save}
              aria-label={`Save ${name}`}
            />
            <DataList.Actions
              items={[{ icon: 'trash', label: `Delete ${name}`, onClick: () => onDelete(lineItem), intent: Intent.DANGER }]}
            />
          </div>
        </DataList.Cell>
      </DataList.Row>
    );
  }
}

export default DefaultItem;
